import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import {
  SUPPLIER_INSTALLATION_EMISSION_REPORT_EXCLUDED_CN_CODE,
} from '@/features/suppliers/lib/supplier-installation-emission-report-cn-code'
import { useCbamGoodsCodesQuery } from '@/features/installations/hooks/useInstallationConfiguration'
import { cn } from '@/shared/lib/utils'

type CnCodeOption = {
  value: string
  label: string
}

type SupplierInstallationEmissionReportCnCodeSelectProps = {
  value: string
  onValueChange: (cnCode: string) => void
  placeholder: string
  disabled?: boolean
  active?: boolean
  id?: string
  triggerClassName?: string
  'aria-invalid'?: boolean
}

function sanitizeCnCodeInput(value: string) {
  return value.replace(/\D/g, '').slice(0, 8)
}

function matchesCnCodePrefix(option: CnCodeOption, query: string): boolean {
  const digits = sanitizeCnCodeInput(query)
  if (!digits) {
    return true
  }
  return option.value.startsWith(digits)
}

export function SupplierInstallationEmissionReportCnCodeSelect({
  value,
  onValueChange,
  placeholder,
  disabled = false,
  active = true,
  id,
  triggerClassName,
  'aria-invalid': ariaInvalid,
}: SupplierInstallationEmissionReportCnCodeSelectProps) {
  const { i18n, t } = useTranslation('installations')
  const goodsQuery = useCbamGoodsCodesQuery({ enabled: active })

  const options = useMemo((): CnCodeOption[] => {
    const rows =
      goodsQuery.data
        ?.filter(
          (good) =>
            good.goodsCode8Digits !==
            SUPPLIER_INSTALLATION_EMISSION_REPORT_EXCLUDED_CN_CODE
        )
        .map((good) => ({
          value: good.goodsCode8Digits,
          label: good.goodsCode8Digits,
        })) ?? []

    rows.sort((optionA, optionB) => optionA.value.localeCompare(optionB.value))
    return rows
  }, [goodsQuery.data])

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  )

  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    if (!active) {
      setInputValue('')
    }
  }, [active])

  const handleInputValueChange = useCallback((next: string) => {
    setInputValue(sanitizeCnCodeInput(next))
  }, [])

  const handleOpenChangeComplete = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setInputValue(value)
      }
    },
    [value]
  )

  const isDisabled = disabled || goodsQuery.isLoading

  return (
    <Combobox
      items={options}
      value={selectedOption}
      inputValue={inputValue}
      onInputValueChange={handleInputValueChange}
      onOpenChangeComplete={handleOpenChangeComplete}
      disabled={isDisabled}
      locale={i18n.language}
      autoHighlight
      itemToStringLabel={(option) => option.label}
      isItemEqualToValue={(optionA, optionB) => optionA.value === optionB.value}
      filter={(item, query) => matchesCnCodePrefix(item as CnCodeOption, query)}
      onValueChange={(option) => {
        if (!option) {
          return
        }
        setInputValue(option.value)
        onValueChange(option.value)
      }}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={isDisabled}
        aria-invalid={ariaInvalid}
        inputMode="numeric"
        className={cn('h-9 w-full min-w-0', triggerClassName)}
      />
      <ComboboxContent className="max-h-[min(24rem,var(--available-height))]">
        <ComboboxEmpty>
          {t(
            'installations:suppliers.edit.installations.edit.emissionReports.fields.noMatchingCnCodes'
          )}
        </ComboboxEmpty>
        <ComboboxList>
          {(option: CnCodeOption) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
